import React, { useMemo, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/Layout';
import { useBetting } from '@/hooks/useBetting';
import { useUserManagement } from '@/hooks/useUserManagement';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/lib/i18n';
import { Match, MatchStatus, BetStatus, UserTier, toMYT } from '@/lib/index';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, TrendingUp, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

// ── helpers ───────────────────────────────────────────────────────────────────

function classifyScore(score: string): 'home' | 'draw' | 'away' | 'aos' {
  if (score.toUpperCase() === 'AOS') return 'aos';
  const parts = score.split('-');
  if (parts.length !== 2) return 'aos';
  const h = parseInt(parts[0], 10);
  const a = parseInt(parts[1], 10);
  if (isNaN(h) || isNaN(a)) return 'aos';
  if (h > a) return 'home';
  if (h === a) return 'draw';
  return 'away';
}

const CANONICAL_SCORE_ORDER = [
  '1-0','2-0','2-1','3-0','3-1','3-2','4-0','4-1','4-2','4-3',
  '0-0','1-1','2-2','3-3','4-4',
  'AOS',
  '0-1','0-2','1-2','0-3','1-3','2-3','0-4','1-4','2-4','3-4',
];

function sortScoreKeys(keys: string[]): string[] {
  const upper = keys.map(k => k.toUpperCase() === 'AOS' ? 'AOS' : k);
  return [...upper].sort((a, b) => {
    const ai = CANONICAL_SCORE_ORDER.indexOf(a);
    const bi = CANONICAL_SCORE_ORDER.indexOf(b);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return 0;
  });
}

// ── types ─────────────────────────────────────────────────────────────────────

/**
 * Per-score aggregate for Focus Prediction 2.
 * All values are computed from raw bets after applying fight% deductions.
 */
interface ScoreData2 {
  /** COMPANY view: sum of bet.amount × (1 - fullChain%) — tickets that remain with company */
  remainingBets: number;
  /** COMPANY view: rebate paid to each downline on remaining bets */
  downlineRebate: number;
  /** AGENT view: sum of fight amounts the viewer takes from their direct downlines (timestamped) */
  fightBets: number;
}

// ── table component ───────────────────────────────────────────────────────────

interface FP2TableProps {
  homeScores: string[];
  drawScores: string[];
  awayScores: string[];
  scoreData: Record<string, ScoreData2>;
  scoreOdds: Record<string, number>;
  /** NIL = no simulation; score string = winning score being simulated */
  simulatedScore: string;
  /** true = viewer is AGENT → show Fight Bets column instead of Remaining Bets */
  viewerIsAgent: boolean;
  /** Agent's own rebate rate (0–1), used for Fight Bets rebate column */
  agentOwnRebateRate: number;
  /** When true, hide the detail columns (Remaining/Fight Bets, Rebate, Amount RM, Focus, Amount Paid) */
  hideDetails: boolean;
}

function FP2Table({
  homeScores, drawScores, awayScores,
  scoreData, scoreOdds, simulatedScore,
  viewerIsAgent, agentOwnRebateRate,
  hideDetails,
}: FP2TableProps) {
  const fmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const isSimulating = simulatedScore !== 'NIL';

  const allScoresBase = [...homeScores, ...drawScores, ...awayScores];
  // Non-NIL: only show rows that have some remaining bets
  const allScores = isSimulating
    ? allScoresBase.filter(sc => (scoreData[sc]?.remainingBets ?? 0) > 0)
    : allScoresBase;

  const categoryOf = (score: string): { label: string; cls: string } => {
    if (homeScores.includes(score)) return { label: 'Home', cls: 'text-blue-600' };
    if (drawScores.includes(score)) return { label: 'Draw', cls: 'text-yellow-600' };
    return { label: 'Away', cls: 'text-red-600' };
  };

  // Per-row calculations
  const calcFocus = (sc: string, remainingBets: number): number => {
    if (!isSimulating) return remainingBets * (scoreOdds[sc] ?? 0);
    return sc === simulatedScore ? remainingBets * (scoreOdds[sc] ?? 0) : 0;
  };

  // Per-row primary value: Fight Bets (agent) or Remaining Bets (company)
  const getPrimary = (sc: string) => viewerIsAgent
    ? (scoreData[sc]?.fightBets ?? 0)
    : (scoreData[sc]?.remainingBets ?? 0);
  // Per-row rebate: Fight Bets × own rebate (agent) or downline rebate (company)
  const getRebate = (sc: string) => viewerIsAgent
    ? (scoreData[sc]?.fightBets ?? 0) * agentOwnRebateRate
    : (scoreData[sc]?.downlineRebate ?? 0);

  // Grand subtotals
  const grandRemainingBets = allScores.reduce((s, sc) => s + getPrimary(sc), 0);
  const grandRebate        = allScores.reduce((s, sc) => s + getRebate(sc), 0);
  const grandAmountRM      = allScores.reduce((s, sc) => {
    return s + (getPrimary(sc) - getRebate(sc));
  }, 0);
  const grandFocus         = allScores.reduce((s, sc) => s + calcFocus(sc, getPrimary(sc)), 0);
  const grandAmountPaid    = allScores.reduce((s, sc) => {
    const rb = getPrimary(sc);
    const re = getRebate(sc);
    const amountRM = rb - re;
    const focus = calcFocus(sc, rb);
    return s + (amountRM - focus);
  }, 0);
  // Possible Win/Loss = Grand Total Amount (RM) - Row Focus
  const footerPossibleWinLoss = grandAmountRM - grandFocus;

  return (
    <Card className="overflow-hidden">
      {allScores.length === 0 ? (
        <div className="px-4 py-4 text-sm text-muted-foreground text-center">—</div>
      ) : (
        <div className="overflow-x-auto text-[6pt] lg:text-base">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/20">
                <th className="text-left px-2 py-2 text-xs font-medium text-muted-foreground w-16">Score</th>
                <th className="text-left px-2 py-2 text-xs font-medium text-muted-foreground w-14">Type</th>
                <th className="text-right px-2 py-2 text-xs font-medium text-muted-foreground w-14">Odds</th>
                {/* Group 1: primary + detail columns — hidden when hideDetails */}
                {!hideDetails && (
                  <th className="text-right px-2 py-2 text-xs font-medium text-muted-foreground whitespace-nowrap bg-blue-50/60 border-l border-blue-200">
                    {viewerIsAgent ? 'Fight Bets' : 'Remaining Bets'}
                  </th>
                )}
                {!hideDetails && (
                  <th className="text-right px-2 py-2 text-xs font-medium text-muted-foreground whitespace-nowrap bg-blue-50/60 border-l border-blue-200">
                    Rebate
                  </th>
                )}
                {!hideDetails && (
                  <th className="text-right px-2 py-2 text-xs font-medium text-muted-foreground whitespace-nowrap bg-blue-100/60 border-l border-blue-300">
                    Amount (RM)
                  </th>
                )}
                {/* Group 2: Focus side */}
                {!hideDetails && (
                  <th className="text-right px-2 py-2 text-xs font-medium text-muted-foreground whitespace-nowrap bg-amber-50/60 border-l border-amber-200">
                    Focus
                  </th>
                )}
                {!hideDetails && (
                  <th className="text-right px-2 py-2 text-xs font-medium text-muted-foreground whitespace-nowrap bg-amber-100/60 border-l border-amber-300">
                    Amount Paid (RM)
                  </th>
                )}
                {/* Result */}
                <th className="text-right px-2 py-2 text-xs font-medium text-muted-foreground whitespace-nowrap border-l border-muted">
                  Possible Win/Loss
                </th>
              </tr>
            </thead>
            <tbody>
              {allScores.map(score => {
                const rb       = getPrimary(score);
                const rebate   = getRebate(score);
                const amountRM = rb - rebate;
                const odd      = scoreOdds[score] ?? 0;
                const focus    = calcFocus(score, rb);
                // Amount Paid (RM) = Amount(RM) - Focus
                const amountPaid = amountRM - focus;
                // Per-row Possible Win/Loss = Grand Total Amount (RM) - this row's Focus
                const possibleWinLoss = grandAmountRM - focus;

                const { label, cls } = categoryOf(score);
                const isWinningScore = isSimulating && score === simulatedScore;

                return (
                  <tr key={score} className={cn(
                    'border-b transition-colors',
                    isWinningScore
                      ? 'bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-950/50'
                      : 'hover:bg-muted/20'
                  )}>
                    <td className="px-2 py-2 whitespace-nowrap">
                      <Badge
                        variant="outline"
                        className={cn('font-bold text-xs px-1.5 py-0',
                          isWinningScore && 'border-amber-500 text-amber-700 dark:text-amber-400')}
                      >
                        {score}{isWinningScore && ' ★'}
                      </Badge>
                    </td>
                    <td className={cn('px-2 py-2 text-xs font-semibold', cls)}>{label}</td>
                    <td className="px-2 py-2 text-right text-xs">
                      <span className="font-semibold text-foreground">{odd.toFixed(2)}</span>
                    </td>

                    {/* Primary: Fight Bets (agent) or Remaining Bets (company) — hidden when hideDetails */}
                    {!hideDetails && (
                      <td className="px-2 py-2 text-right text-xs font-semibold text-black tabular-nums whitespace-nowrap bg-blue-50/40 border-l border-blue-200">
                        {fmt(rb)}
                      </td>
                    )}
                    {/* Rebate */}
                    {!hideDetails && (
                      <td className="px-2 py-2 text-right text-xs font-semibold text-black tabular-nums whitespace-nowrap bg-blue-50/40 border-l border-blue-200">
                        {fmt(rebate)}
                      </td>
                    )}
                    {/* Amount (RM) = Remaining - Rebate */}
                    {!hideDetails && (
                      <td className="px-2 py-2 text-right text-xs font-bold text-black tabular-nums whitespace-nowrap bg-blue-100/40 border-l border-blue-300">
                        {fmt(amountRM)}
                      </td>
                    )}
                    {/* Focus */}
                    {!hideDetails && (
                      <td className="px-2 py-2 text-right text-xs font-semibold text-black tabular-nums whitespace-nowrap bg-amber-50/40 border-l border-amber-200">
                        {fmt(focus)}
                      </td>
                    )}
                    {/* Amount Paid (RM) = Amount(RM) - Focus */}
                    {!hideDetails && (
                      <td className={cn(
                        'px-2 py-2 text-right text-xs font-bold tabular-nums whitespace-nowrap bg-amber-100/40 border-l border-amber-300',
                        amountPaid < 0 ? 'text-red-600' : 'text-black'
                      )}>
                        {fmt(amountPaid)}
                      </td>
                    )}

                    {/* Possible Win/Loss per row = grandAmountRM + this row's Amount Paid */}
                    <td className={cn(
                      'px-2 py-2 text-right text-xs font-bold tabular-nums whitespace-nowrap border-l border-muted',
                      possibleWinLoss < 0 ? 'text-red-600' : 'text-black'
                    )}>
                      {fmt(possibleWinLoss)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t bg-muted/30">
                <td className="px-2 py-2 text-xs text-muted-foreground font-medium" colSpan={3}>Total</td>
                {!hideDetails && (
                  <td className="px-2 py-2 text-right text-xs font-bold text-black tabular-nums bg-blue-50/40 border-l border-blue-200">
                    {fmt(grandRemainingBets)}
                  </td>
                )}
                {!hideDetails && (
                  <td className="px-2 py-2 text-right text-xs font-bold text-black tabular-nums bg-blue-50/40 border-l border-blue-200">
                    {fmt(grandRebate)}
                  </td>
                )}
                {!hideDetails && (
                  <td className="px-2 py-2 text-right text-xs font-bold text-black tabular-nums bg-blue-100/40 border-l border-blue-300">
                    {fmt(grandAmountRM)}
                  </td>
                )}
                {!hideDetails && (
                  <td className="px-2 py-2 text-right text-xs font-bold text-black tabular-nums bg-amber-50/40 border-l border-amber-200">
                    {fmt(grandFocus)}
                  </td>
                )}
                {!hideDetails && (
                  <td className={cn(
                    'px-2 py-2 text-right text-xs font-bold tabular-nums bg-amber-100/40 border-l border-amber-300',
                    grandAmountPaid < 0 ? 'text-red-600' : 'text-black'
                  )}>
                    {fmt(grandAmountPaid)}
                  </td>
                )}
                <td className={cn(
                  'px-2 py-2 text-right text-xs font-bold tabular-nums border-l border-muted',
                  footerPossibleWinLoss < 0 ? 'text-red-600' : 'text-black'
                )}>
                  {fmt(footerPossibleWinLoss)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Card>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────

export default function PreMatchSummary2() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { matches, bets, isLoading } = useBetting();
  const { users: allUsers, getFightSetting } = useUserManagement();

  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  // Score simulation: 'NIL' = no simulation, otherwise the winning score being simulated
  const [simulatedScore, setSimulatedScore] = useState<string>('NIL');
  // Match history view (shows only COMPLETED matches)
  const [showHistory, setShowHistory] = useState(false);
  // Column visibility toggle: when true, hide Remaining/Fight Bets, Rebate, Amount(RM), Focus, Amount Paid
  const [hideDetails, setHideDetails] = useState(false);

  // Timestamped fight settings history for viewer (agent→agent lookup)
  interface FightHistoryRow {
    parent_id: string;
    child_id: string;
    fight_on_off: boolean;
    fight_pct: number;
    valid_from: string;
    valid_to: string | null;
  }
  const [fightHistory, setFightHistory] = useState<FightHistoryRow[]>([]);
  useEffect(() => {
    if (!user?.id || user.tier !== UserTier.AGENT) return;
    supabase
      .from('fight_settings_history')
      .select('parent_id, child_id, fight_on_off, fight_pct, valid_from, valid_to')
      .eq('parent_id', user.id)
      .then(({ data }) => { if (data) setFightHistory(data as FightHistoryRow[]); });
  }, [user?.id, user?.tier]);

  // Company/Master or Agent can access this page
  const canAccess = !!user && (
    user.tier === UserTier.COMPANY ||
    user.tier === UserTier.MASTER ||
    user.tier === UserTier.AGENT
  );
  // Active matches (non-completed) shown in main list
  const upcomingMatches = useMemo(() =>
    matches.filter(m =>
      m.status === MatchStatus.UPCOMING ||
      m.status === MatchStatus.LIVE
    ), [matches]);

  // Completed matches shown in history view — latest first
  const completedMatchesList = useMemo(() =>
    matches
      .filter(m =>
        m.status === MatchStatus.COMPLETED &&
        m.scoreOdds &&
        Object.keys(m.scoreOdds).length > 0
      )
      .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()),
  [matches]);

  // Reset simulation when match changes
  useEffect(() => {
    setSimulatedScore('NIL');
  }, [selectedMatch?.id]);

  // ── scoreData computation (TIMESTAMPED fight% via fightPctSnapshotChain) ──
  //
  // COMPANY view:  remainingBets = amount × (1 - fullChain%)  [full chain stored at bet time]
  //                downlineRebate = Σ remaining × direct-child rebate rate
  //
  // AGENT view:    fightBets = Σ (amount flowing into direct child) × viewer's fight% vs that child
  //                  For each bet:
  //                    fractionAtCompany = 1 - chainPct/100
  //                    fractionIntoDirectChild = fractionAtCompany / (aboveViewerFraction × (1 - viewerFight/100))
  //                    fightAmount = amount × fractionIntoDirectChild × (viewerFight/100)
  //                  Simplified: fightAmount = remaining_at_viewer × viewerFight / (1 - viewerFight)
  //                  where remaining_at_viewer = fractionAtCompany / aboveViewerFraction
  //                rebate = fightBets × agent's own rebate rate (computed in FP2Table)
  const scoreData = useMemo((): Record<string, ScoreData2> => {
    if (!selectedMatch || !user) return {};
    const result: Record<string, ScoreData2> = {};

    // Initialise all scores from the match
    for (const score of Object.keys(selectedMatch.scoreOdds ?? {})) {
      result[score] = { remainingBets: 0, downlineRebate: 0, fightBets: 0 };
    }

    // Build userId → user lookup
    const userById = new Map(allUsers.map(u => [u.id, u]));

    const viewerIsCompany =
      user.tier === UserTier.COMPANY || user.tier === UserTier.MASTER;

    // For AGENT viewers: compute the combined fight fraction of all ancestors
    // ABOVE the viewer (viewer's parent up to company), using live getFightSetting.
    // This represents how much of the original bet the viewer's parent level has
    // already taken — we use it to reconstruct the flow INTO the viewer's children.
    let aboveViewerFraction = 1.0; // product of (1 - parentFight%) for ancestors above viewer
    if (!viewerIsCompany) {
      let currentChildId = user.id;
      const visited = new Set<string>([user.id]);
      while (true) {
        const current = userById.get(currentChildId);
        if (!current || !current.parentId) break;
        if (visited.has(current.parentId)) break;
        visited.add(current.parentId);
        const parent = userById.get(current.parentId);
        if (!parent) break;
        const fs = getFightSetting(current.parentId, currentChildId);
        const fightPct = fs.fightOnOff ? (fs.fightPct ?? 0) : 0;
        if (fightPct > 0) aboveViewerFraction *= (1 - fightPct / 100);
        if (parent.tier === UserTier.COMPANY || parent.tier === UserTier.MASTER) break;
        currentChildId = current.parentId;
      }
    }

    // Build the viewer's full subtree
    const viewerSubtree = new Set<string>();
    const buildSubtree = (pid: string) => {
      for (const u of allUsers) {
        if (u.parentId === pid) { viewerSubtree.add(u.id); buildSubtree(u.id); }
      }
    };
    buildSubtree(user.id);

    // All non-cancelled bets for this match
    const matchBets = bets.filter(b =>
      b.matchId === selectedMatch.id &&
      b.status !== BetStatus.CANCELLED
    );

    for (const bet of matchBets) {
      if (!viewerSubtree.has(bet.userId)) continue;

      // Normalise 'Any Other Score' → 'AOS'
      const betSelection = bet.selection === 'Any Other Score' ? 'AOS' : bet.selection;
      if (!result[betSelection]) result[betSelection] = { remainingBets: 0, downlineRebate: 0, fightBets: 0 };

      // fightPctSnapshotChain = full compounded chain% stored at bet placement (timestamped)
      const chainPct = bet.fightPctSnapshotChain ?? bet.fightPctSnapshot ?? 0;
      const fractionAtCompany = 1 - chainPct / 100; // fraction reaching company

      if (viewerIsCompany) {
        // ── COMPANY VIEW ──────────────────────────────────────────────────────
        // Remaining = amount × (1 - fullChain%)
        const remainingForBet = bet.amount * Math.max(0, fractionAtCompany);

        // Find which direct child of the company this bet flows through
        let directChildId: string | null = null;
        let walkId = bet.userId;
        const walkVisited = new Set<string>();
        while (walkId) {
          walkVisited.add(walkId);
          const wu = userById.get(walkId);
          if (!wu || !wu.parentId) break;
          if (wu.parentId === user.id) { directChildId = walkId; break; }
          if (walkVisited.has(wu.parentId)) break;
          walkId = wu.parentId;
        }
        const directChild = directChildId ? userById.get(directChildId) : null;
        const childRebateRate = directChild ? (1 - (directChild.ticketPrice ?? 1)) : 0;

        result[betSelection].remainingBets  += remainingForBet;
        result[betSelection].downlineRebate += remainingForBet * childRebateRate;

      } else {
        // ── AGENT VIEW ────────────────────────────────────────────────────────
        // Fight Bets differ by whether the DIRECT child is a PLAYER or an AGENT:
        //
        // • Direct child is PLAYER:
        //     fightAmount = bet.amount × bet.fightPctSnapshot%
        //     (timestamped fight% set by this viewer vs the player at bet time)
        //
        // • Direct child is AGENT:
        //     childRemaining = amount × (fractionAtCompany / aboveViewerFraction)
        //     fightAmount = childRemaining × viewerFightPct% (live fight% for agent→agent)
        //
        // Rebate = Fight Bets × viewer's own rebate rate (computed in FP2Table)

        // Find which direct child of the viewer this bet flows through
        let directChildId: string | null = null;
        let walkId = bet.userId;
        const walkVisited = new Set<string>();
        while (walkId) {
          walkVisited.add(walkId);
          const wu = userById.get(walkId);
          if (!wu || !wu.parentId) break;
          if (wu.parentId === user.id) { directChildId = walkId; break; }
          if (walkVisited.has(wu.parentId)) break;
          walkId = wu.parentId;
        }
        if (!directChildId) continue;

        const directChild = directChildId ? userById.get(directChildId) : null;
        const directChildIsPlayer = directChild?.tier === UserTier.PLAYER;

        let fightAmount = 0;

        if (directChildIsPlayer) {
          // ── Player direct child: use TIMESTAMPED fightPctSnapshot on the bet ──
          // fightPctSnapshot stores the viewer's fight% vs this player at bet placement time
          const snapshotPct = bet.fightPctSnapshot ?? 0;
          if (snapshotPct <= 0) continue; // no fight at bet time → skip
          fightAmount = bet.amount * snapshotPct / 100;
        } else {
          // ── Agent direct child: use TIMESTAMPED fight% from fight_settings_history ──
          // Look up what fight% the viewer had set against this child at bet placement time
          const betTime = new Date(bet.createdAt).getTime();
          const historyRows = fightHistory.filter(h => h.child_id === directChildId);

          // Find the row whose valid_from <= betTime < valid_to (or valid_to is null = still active)
          const activeRow = historyRows.find(h => {
            const from = new Date(h.valid_from).getTime();
            const to   = h.valid_to ? new Date(h.valid_to).getTime() : Infinity;
            return betTime >= from && betTime < to;
          });

          // Fall back to live setting if no history row found
          const viewerFightPct = activeRow
            ? (activeRow.fight_on_off ? activeRow.fight_pct : 0)
            : (() => { const vFs = getFightSetting(user.id, directChildId); return vFs.fightOnOff ? (vFs.fightPct ?? 0) : 0; })();

          if (viewerFightPct <= 0) continue; // no fight at bet time → skip

          // childRemaining = bet amount after all sub-chain fights (timestamped)
          const childRemaining = aboveViewerFraction > 0
            ? bet.amount * fractionAtCompany / aboveViewerFraction
            : bet.amount * fractionAtCompany;

          // Per formula: fight = childRemaining × viewerFightPct%  (250 × 5% = 12.5)
          fightAmount = childRemaining * viewerFightPct / 100;
        }

        result[betSelection].fightBets += Math.max(0, fightAmount);
      }
    }

    return result;
  }, [selectedMatch, bets, allUsers, user, getFightSetting, fightHistory]);

  // scoreOdds convenience map (positive-odds scores + AOS always included)
  const scoreOdds = useMemo(() =>
    selectedMatch?.scoreOdds
      ? Object.fromEntries(
          Object.entries(selectedMatch.scoreOdds)
            .filter(([k, v]) => (v ?? 0) > 0 || k.toUpperCase() === 'AOS' || k === 'Any Other Score')
            .map(([k, v]) => [k === 'Any Other Score' ? 'AOS' : k, v ?? 0])
        )
      : {}, [selectedMatch]);

  // Split scores into home / draw+AOS / away
  const { homeScores, drawScores, awayScores } = useMemo(() => {
    if (!selectedMatch?.scoreOdds) return { homeScores: [], drawScores: [], awayScores: [] };
    const valid = Object.keys(selectedMatch.scoreOdds).filter(k => {
      const normKey = k.toUpperCase() === 'AOS' || k === 'Any Other Score' ? 'AOS' : k;
      // AOS is always included (odds may be 0 as placeholder); exclude 5-5
      if (normKey === 'AOS') return true;
      return (selectedMatch.scoreOdds![k] ?? 0) > 0 && k !== '5-5';
    // Normalise to AOS key
    }).map(k => k === 'Any Other Score' ? 'AOS' : k);
    const home: string[] = [], draw: string[] = [], away: string[] = [];
    for (const k of valid) {
      const cat = classifyScore(k);
      if (cat === 'home') home.push(k);
      else if (cat === 'draw' || cat === 'aos') draw.push(k);
      else away.push(k);
    }
    return { homeScores: sortScoreKeys(home), drawScores: sortScoreKeys(draw), awayScores: sortScoreKeys(away) };
  }, [selectedMatch]);

  // Is the viewer an agent (not company/master)?
  const viewerIsAgent = !!user && user.tier === UserTier.AGENT;
  // Agent's own rebate rate: ticketPrice in DB is decimal (e.g. 0.96 = 4% rebate, 1.0 = 0%)
  // So rebateRate = 1 - ticketPrice  (e.g. 1 - 0.96 = 0.04 = 4%)
  const agentOwnRebateRate = user ? Math.max(0, 1 - (user.ticketPrice ?? 1)) : 0;

  // Grand total label value: Fight Bets (agent) or Remaining Bets (company)
  const grandRemainingTotal = useMemo(() => {
    const tableScores = [...homeScores, ...drawScores, ...awayScores];
    if (viewerIsAgent) {
      return tableScores.reduce((s, sc) => s + (scoreData[sc]?.fightBets ?? 0), 0);
    }
    return tableScores.reduce((s, sc) => s + (scoreData[sc]?.remainingBets ?? 0), 0);
  }, [scoreData, homeScores, drawScores, awayScores, viewerIsAgent]);

  // ── Match list view ───────────────────────────────────────────────────────
  const renderMatchCards2 = (matchList: typeof upcomingMatches, isHistoryView: boolean) => (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {matchList.map(match => {
        const start = new Date(match.startTime);
        const isClosed = !isHistoryView && new Date() >= new Date(start.getTime() - 5 * 60 * 1000);
        const oddsCount = Object.keys(match.scoreOdds ?? {}).filter(k => (match.scoreOdds![k] ?? 0) > 0).length;
        return (
          <Card
            key={match.id}
            className={cn('cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02]', isClosed && 'opacity-60')}
            onClick={() => setSelectedMatch(match)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-sm md:text-base flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-primary shrink-0" />
                  {match.homeTeam} vs {match.awayTeam}
                </CardTitle>
                <Badge
                  variant={isHistoryView ? 'outline' : isClosed ? 'destructive' : 'secondary'}
                  className={cn('shrink-0', isHistoryView && 'border-green-500 text-green-700')}
                >
                  {isHistoryView ? `✓ ${match.result ?? 'Completed'}` : isClosed ? 'Closed' : match.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground space-y-1">
              <p>{format(toMYT(new Date(match.startTime)), 'PPp')} MYT</p>
              <p>{oddsCount} odds available</p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  const matchListView = (
    <div className="space-y-6 p-4 md:p-6 pt-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-muted-foreground text-sm">
          {t.nav.focusPrediction2} — {t.preMatch.pageSubtitle}
        </p>
        <Button
          variant="outline" size="sm"
          onClick={() => setShowHistory(true)}
          className="h-8 text-xs px-3 gap-1.5 border border-amber-400 text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/30 shrink-0"
        >
          🏆 Match History ({completedMatchesList.length})
        </Button>
      </div>
      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">{t.common.loading}</div>
      ) : upcomingMatches.length === 0 ? (
        <div className="text-center py-12">
          <div className="mb-4 text-4xl">⚽</div>
          <p className="text-muted-foreground">{t.preMatch.noMatches}</p>
        </div>
      ) : (
        renderMatchCards2(upcomingMatches, false)
      )}
    </div>
  );

  // ── History view ──────────────────────────────────────────────────────────
  const historyView2 = (
    <div className="space-y-6 p-4 md:p-6 pt-4">
      <div className="flex items-center gap-3">
        <Button
          variant="outline" size="sm"
          onClick={() => setShowHistory(false)}
          className="h-7 text-[10px] px-2 gap-1 rounded bg-[rgb(125,225,129)] border border-input hover:bg-accent hover:text-accent-foreground shrink-0"
        >
          <ArrowLeft className="h-3 w-3" />
          {t.preMatch.backToMatches}
        </Button>
        <div>
          <h2 className="text-sm font-bold">🏆 Match History</h2>
          <p className="text-[10px] text-muted-foreground">{completedMatchesList.length} completed match{completedMatchesList.length !== 1 ? 'es' : ''}</p>
        </div>
      </div>
      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">{t.common.loading}</div>
      ) : completedMatchesList.length === 0 ? (
        <div className="text-center py-12">
          <div className="mb-4 text-4xl">🏆</div>
          <p className="text-muted-foreground">No completed matches yet.</p>
        </div>
      ) : (
        renderMatchCards2(completedMatchesList, true)
      )}
    </div>
  );

  // ── Detail view ───────────────────────────────────────────────────────────
  const detailView = selectedMatch && (
    <div className="space-y-4 p-4 md:p-6 pt-4">
      {/* Back + title */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline" size="sm"
          onClick={() => setSelectedMatch(null)}
          className="h-6 text-[10px] px-1.5 gap-1 rounded bg-[rgb(125,225,129)] border border-input hover:bg-accent hover:text-accent-foreground shrink-0"
        >
          <ArrowLeft className="h-3 w-3" />
          {t.preMatch.backToMatches}
        </Button>
        <div>
          <h2 className="text-xs md:text-xl font-bold tracking-tight">
            {selectedMatch.homeTeam} vs {selectedMatch.awayTeam}
          </h2>
          <p className="text-[10px] text-muted-foreground">
            {format(toMYT(selectedMatch.startTime), 'PPpp')} MYT
          </p>
        </div>
      </div>

      {/* Grand total card */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-3 flex items-center gap-3">
          <TrendingUp className="h-5 w-5 text-primary shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">
              {viewerIsAgent ? 'Total Fight Bets (All Scores)' : 'Total Remaining Bets (All Scores)'}
            </p>
            <p className="text-lg font-bold text-primary">
              {grandRemainingTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Score Simulation + table */}
      <div className="space-y-3">
        <div className="flex items-center gap-3 px-1 flex-wrap">
          <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">Score Simulation:</span>
          <div className="relative">
            <select
              value={simulatedScore}
              onChange={e => setSimulatedScore(e.target.value)}
              className="text-xs border rounded px-2 py-1.5 pr-7 bg-background hover:bg-muted focus:outline-none focus:ring-1 focus:ring-primary appearance-none cursor-pointer min-w-[110px] font-medium"
            >
              <option value="NIL">NIL</option>
              {[...homeScores, ...drawScores, ...awayScores].map(sc => (
                <option key={sc} value={sc}>{sc}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          </div>
          {/* Toggle detail columns button */}
          <button
            onClick={() => setHideDetails(h => !h)}
            title={hideDetails ? 'Show detail columns' : 'Hide detail columns'}
            className={[
              'h-6 w-6 flex items-center justify-center rounded border text-xs font-bold transition-colors shrink-0',
              hideDetails
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background text-muted-foreground border-input hover:bg-muted hover:text-foreground',
            ].join(' ')}
          >
            {hideDetails ? '−' : '+'}
          </button>
          {simulatedScore !== 'NIL' && (
            <div className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded px-2 py-1">
              <span className="font-semibold">★ Simulating: {simulatedScore} wins</span>
              <span className="text-muted-foreground">— Focus for other scores set to 0</span>
            </div>
          )}
        </div>

        <FP2Table
          homeScores={homeScores}
          drawScores={drawScores}
          awayScores={awayScores}
          scoreData={scoreData}
          scoreOdds={scoreOdds}
          simulatedScore={simulatedScore}
          viewerIsAgent={viewerIsAgent}
          agentOwnRebateRate={agentOwnRebateRate}
          hideDetails={hideDetails}
        />
      </div>
    </div>
  );

  // Guard: only Company/Master/Agent can see this page
  if (!canAccess) {
    return (
      <Layout>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <p className="text-muted-foreground text-sm">Access restricted to Company / Master / Agent accounts.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-background text-[13px] lg:text-base">
        <div className="border-b bg-card px-4 md:px-6 py-4">
          <div className="flex items-center gap-3">
            <TrendingUp className="h-5 w-5 text-primary" />
            <div>
              <h1 className="text-lg font-bold">{t.nav.focusPrediction2}</h1>
              {!selectedMatch && !showHistory && (
                <p className="text-xs text-muted-foreground">{t.preMatch.selectMatch}</p>
              )}
              {showHistory && !selectedMatch && (
                <p className="text-xs text-muted-foreground">Viewing completed matches</p>
              )}
            </div>
          </div>
        </div>
        {selectedMatch ? detailView : showHistory ? historyView2 : matchListView}
      </div>
    </Layout>
  );
}